/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Popover, Row, Space, Tabs, Tooltip, Typography } from 'antd';
import { CopyOutlined, PlusCircleOutlined } from '@ant-design/icons';
import NodeAppearances from '../UI/NodeAppearances';
import { GraphContext } from './FlowGraph';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';

// Misc imports
import { PERMISSIONS_GROUP_EDITOR } from '../../constants/main';
import { CAROUSEL_NODE_HANDLE_IDS, GRAPH_LAYOUTS, NODE_TYPES, NODE_TYPES_PROPERTIES, SWITCH_QUERY_HANDLE_IDS, USER_INPUT_QUERY_HANDLE_IDS } from '../../constants/flowGraph';
import { calculateNodePriority, getValidSourceNodeTypes, getValidTargetNodeTypes } from '../../utils/transform/flowGraph';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { IS_USER_FEEDBACK_ENABLED } from '../../constants/env';
import { useUpdateActivityMutation } from '../../store/activities/activitiesApi';
import { isNodeDeprecated } from '../../utils/is/isNodeDeprecated';
import is from '../../utils/is';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

// Constant declarations
const { TabPane } = Tabs;
const NEW_NODE_PLACEMENT = {
  BEFORE: 'before',
  AFTER: 'after'
};

const NodeActionsTooltip = ({ id, isVisible, isEditEnabled, isDeleteEnabled, isCloneEnabled, isAddNodeTooltipOpen, setisAddNodeTooltipOpen, selected, type }) => {
  const { openEditForm, addSelectedNodes, deleteSelectedNodes, cloneSelectedElements, disabledNodeTypes } = useContext(GraphContext);
  const { isEditingPrimaryLanguage } = useBotLanguage();

  const renderNodeActions = () => {
    const actions = [];
    const isDeprecated = isNodeDeprecated(type, disabledNodeTypes);

    // Edit icon
    if (isEditEnabled) {
      actions.push(
        <Button
          type="text"
          icon={<EditIcon />}
          className="border-0 text-white"
          onClick={() => openEditForm(id, { propertyToFocus: null, openedFromTooltip: true })}
        />
      );
    }

    // Delete icon
    if (isDeleteEnabled && isEditingPrimaryLanguage) {
      actions.push(
        <Button
          type="text"
          icon={<DeleteIcon />}
          className="border-0 text-white"
          onClick={() => {
            // If the affected node is not selected, only select this one, otherwise affect the entire selection
            if (!selected) {
              addSelectedNodes(id);
            }
            deleteSelectedNodes();
          }}
        />
      );
    }

    // Clone icon
    if (isCloneEnabled && isEditingPrimaryLanguage) {
      actions.push(
        <Button
          type="text"
          icon={
            <Fragment>
              <Tooltip
                title={isDeprecated ? `Node ${NODE_TYPES_PROPERTIES[type]?.label } is deprecated` : ''}
              >
                <CopyOutlined />
              </Tooltip>
            </Fragment>
          }
          className={classNames('border-0 text-white', { 'opacity-50 cursor-not-allowed': isDeprecated })}
          onClick={() => {
            if(!isDeprecated) {
              if (!selected) {
                addSelectedNodes(id);
              }
              // If the affected node is not selected, only select this one, otherwise affect the entire selection
              cloneSelectedElements();
            }
          }}
        />
      );
    }

    // Add node suggestions will be visible on all nodes - Sticky notes shouldn't have the plus icon to add nodes before/after
    if (isEditingPrimaryLanguage && type !== NODE_TYPES.STICKY_NOTE) {
      actions.push(
        <Button
          type="text"
          icon={<PlusCircleOutlined />}
          className="border-0 text-white"
          onClick={() => setisAddNodeTooltipOpen(!isAddNodeTooltipOpen)}
        />
      );
    }

    // If we have 4 actions, align them as a square
    const isSquare = actions.length === 4;

    return (
      <Row
        className={classNames('node-tooltip nodrag bg-black', { 'd-none': !isVisible })}
        style={isSquare && { width: 68 }}
        gutter={1}
      >
        {
          actions.map((action, index) => {
            const isLastRow = (isSquare && (index === 2 || index === 3)) || (!isSquare && index === actions.length - 1);
            const isLastCol = (isSquare && (index === 1 || index === 3)) || !isSquare;

            return (
              <Col
                key={index}
                className={classNames({
                  'border-bottom': !isLastRow,
                  'border-right': !isLastCol
                })}
                {...isSquare && { span: 12 }}
              >
                {action}
              </Col>
            );
          })
        }
      </Row>
    );
  };

  return renderNodeActions();
};

export const NodeSuggestionsPanel = ({
  id,
  type,
  className,
  isAddNodeTooltipOpen,
  isBeforeTabVisible = true,
  sourceHandleToConnect,
  onAfterAdd = () => {},
  onSelect = async () => {}
}) => {
  const { elements, addNewElements, graphDirection, disabledNodeTypes } = useContext(GraphContext);
  const { t } = useTranslation();
  const { primaryBotLanguage } = useBotLanguage();

  const onAddNode = async (currentNodeId, newNodeType, newNodePlacement) => {
    const currentNode = elements.find(el => el.id === currentNodeId);
    const newNodeId = uuidv4();

    let sourceHandle;
    let priority;
    if (NEW_NODE_PLACEMENT.AFTER) {
      // When an "Option" node becomes connected, its priority needs to be recalculated
      if ([NODE_TYPES.OPTION, NODE_TYPES.LINK_OPTION].includes(newNodeType)) {
        priority = calculateNodePriority(elements, currentNode);
      }

      if ([NODE_TYPES.QUERY, NODE_TYPES.SWITCH_QUERY].includes(currentNode.type)) {
        const currentEdges = elements.filter(el => el.source === currentNodeId);
        // Initialize to true handle for cases of no connection at all or false handles.
        sourceHandle = SWITCH_QUERY_HANDLE_IDS.TRUE;
        // If a node is connected to the true handle, new node should be connected to the false handle
        if (currentEdges.find((edge) => edge.sourceHandle === SWITCH_QUERY_HANDLE_IDS.TRUE)) {
          sourceHandle = SWITCH_QUERY_HANDLE_IDS.FALSE;
        }
      }

      if (currentNode.type === NODE_TYPES.INPUT_QUERY) {
        const currentEdges = elements.filter(el => el.source === currentNodeId);
        // Initialize to true handle for cases of no connection at all or skip handle.
        sourceHandle = USER_INPUT_QUERY_HANDLE_IDS.TRUE;
        // If a node is connected to the true handle, new node should be connected to the skip handle
        if (currentEdges.find((edge) => edge.sourceHandle === USER_INPUT_QUERY_HANDLE_IDS.TRUE)) {
          sourceHandle = USER_INPUT_QUERY_HANDLE_IDS.FALSE;
        }
      }

      // Handle cases where the user auto-adds a node after a carousel or file upload node
      // These nodes might have more than one handle, so we need to specify the ID
      if (currentNode.type === NODE_TYPES.CAROUSEL) {
        await onSelect();

        sourceHandle = CAROUSEL_NODE_HANDLE_IDS.NEXT;

        // Handle case where we add a new node from a card button
        if (sourceHandleToConnect) {
          sourceHandle = sourceHandleToConnect;
        }
      }
      if (currentNode.type === NODE_TYPES.FILE_UPLOAD) {
        sourceHandle = currentNodeId;
      }
    }

    let newNode = {
      id: newNodeId,
      type: newNodeType,
      data: {
        label: { [primaryBotLanguage]: '' },
        nodeType: 'node',
        ...(priority && { priority: priority })
      },
      ...(currentNode && {
        position: {
          x: graphDirection === GRAPH_LAYOUTS.HORIZONTAL ? (newNodePlacement === NEW_NODE_PLACEMENT.BEFORE ? currentNode.position.x - 215 : currentNode.position.x + currentNode.width + 100) : currentNode.position.x,
          y: graphDirection === GRAPH_LAYOUTS.VERTICAL ? (newNodePlacement === NEW_NODE_PLACEMENT.BEFORE ? currentNode.position.y - 110 : currentNode.position.y + + currentNode.height + 50) : currentNode.position.y
        }
      })
    };

    // TODO: Validate edge connection before adding the new node or come up with a more global solution
    let newEdge;

    // If a placement is specified, add a new node before/after the current one
    if (newNodePlacement) {
      newEdge = {
        id: uuidv4(),
        type: 'default',
        arrowHeadType: 'arrowclosed',
        data: {
          label: null,
          nodeType: 'edge'
        },
        ...(sourceHandle && { sourceHandle: sourceHandle }),
        ...(newNodePlacement === NEW_NODE_PLACEMENT.BEFORE && { source: newNodeId, target: currentNodeId }),
        ...(newNodePlacement === NEW_NODE_PLACEMENT.AFTER && { source: currentNodeId, target: newNodeId })
      };
    }

    addNewElements({
      nodes: [newNode],
      ...(newEdge && { edges: [newEdge] })
    });

    onAfterAdd(newNodeId);
  };

  const renderNodeSuggestions = (nodesArray, newNodePlacement) => {
    if (nodesArray && !is.emptyArray(nodesArray)) {
      // Remove user feedback node if the feature is disabled
      if (nodesArray.some(nodeType => nodeType === NODE_TYPES.USER_FEEDBACK) && !IS_USER_FEEDBACK_ENABLED) {
        nodesArray = nodesArray.filter(nodeType => nodeType !== NODE_TYPES.USER_FEEDBACK);
      }

      return (
        <Space
          direction="vertical"
          className="text-left overflow-y-auto overflow-x-hidden scrollbar-custom scrollbar-custom-nohide scrollbar-custom-dark"
          style={{ maxHeight: 235 }}
        >
          {
            nodesArray.map(nodeType => {
              const node = NODE_TYPES_PROPERTIES[nodeType];
              return (
                <Button
                  key={nodeType}
                  type="text"
                  onClick={() => onAddNode(id, node.type, newNodePlacement)}
                >
                  <Space>
                    {node.icon}
                    {node.label}
                  </Space>
                </Button>
              );
            })
          }
        </Space>
      );
    }

    // Else
    return (
      <Typography.Text className="text-white fs-sm white-space-nowrap py-0">
        {t('(No nodes available)')}
      </Typography.Text>
    );
  };

  return (
    <Space
      className={classNames('node-tooltip nodrag node-tooltip__suggestions', {
        'd-none': !isAddNodeTooltipOpen,
        [className]: className
      })}
      style={{ minWidth: 200 }}
      align="start"
    >
      {
        isBeforeTabVisible
          ? <Tabs defaultActiveKey={NEW_NODE_PLACEMENT.AFTER} tabPosition="left">
            <TabPane tab={t('Before')} key={NEW_NODE_PLACEMENT.BEFORE}>
              <Space direction="vertical" className="text-left">
                {renderNodeSuggestions(getValidSourceNodeTypes(type, disabledNodeTypes), NEW_NODE_PLACEMENT.BEFORE)}
              </Space>
            </TabPane>

            <TabPane tab={t('After')} key={NEW_NODE_PLACEMENT.AFTER}>
              <Space direction="vertical" className="text-left">
                {renderNodeSuggestions(getValidTargetNodeTypes(id, elements, type, disabledNodeTypes), NEW_NODE_PLACEMENT.AFTER)}
              </Space>
            </TabPane>
          </Tabs> : <Space direction="vertical" className="text-left">
            {renderNodeSuggestions(getValidTargetNodeTypes(id, elements, type, disabledNodeTypes), NEW_NODE_PLACEMENT.AFTER)}
          </Space>
      }
    </Space>
  );
};

const NodeWrapper = ({ id, type, children, isEditEnabled = true, isDeleteEnabled = true, isCloneEnabled = true, dragging, selected, data }) => {
  const { tenantId, orgId } = useParams();
  const { isEditingPrimaryLanguage } = useBotLanguage();
  const { openEditForm } = useContext(GraphContext);

  const userPermissions = useUserPermissions(orgId, tenantId);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [, { error: updateActivityError }] = useUpdateActivityMutation({ fixedCacheKey: 'flowGraph-update-activity' });

  const canEdit = userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission)) && data?.appearances === undefined && !data?.isViewOnlyMode;
  const hasAppearances = data?.appearances !== undefined;

  const [hasError, setHasError] = useState(false);
  const [isActionsTooltipVisible, setIsActionsTooltipVisible] = useState(false);
  const [isAddNodeTooltipOpen, setisAddNodeTooltipOpen] = useState(false);
  const [popoverPlacement, setPopoverPlacement] = useState('rightTop');

  const wrapperRef = useRef();

  useEffect(() => {
    if (updateActivityError) {
      setHasError((updateActivityError.error?.metadata?.failingNodes || []).some(node => node.id === id));
    } else {
      setHasError(false);
    }
  }, [updateActivityError]);

  // On unmount
  useEffect(() => {
    setHasError(false);
  }, [searchParams.get('activity')]);

  useEffect(() => {
    if (isAddNodeTooltipOpen) {
      // When made visible, re-check positioning if needed
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      const flowgraphContainer = document.querySelector('.conversation-graph');

      if (!flowgraphContainer) {
        return;
      }

      const containerRect = flowgraphContainer.getBoundingClientRect();
      const availableSpaceOnRight = containerRect.right - wrapperRect.right;

      // If there's not enough space inside the flowgraph container (right), place popover on top
      if (availableSpaceOnRight < 400) {
        setPopoverPlacement('topRight');
      } else {
        setPopoverPlacement('rightTop');
      }
    }
  }, [isAddNodeTooltipOpen]);

  const nodeAppearances = useMemo(() => {
    if (hasAppearances) {
      return data.appearances;
    }
    return {};
  }, [id]);

  return (
    <span
      /*
      * Use of 'nopan' class allows click events on nodes that are not be draggable
      * https://github.com/xyflow/xyflow/issues/2525
      *
      * Also, we are using `has-backend-error` for errors that come from the backend
      */
      ref={wrapperRef}
      className={classNames('react-flow__node__nodeWrapper', `react-flow__node-${type}__nodeWrapper`, {
        'has-backend-error': hasError,
        'nopan': !canEdit || !isEditingPrimaryLanguage
      })}
      onMouseEnter={() => {
        if (!dragging) {
          setIsActionsTooltipVisible(true);
        }
      }}
      onMouseLeave={() => {
        // See if the user is trying to create a new edge (thus having isMouseDown = true but still being out of node bounds)
        const connectionLineInDom = document.querySelector('.conversation-graph .react-flow__connectionline');

        if (!dragging || connectionLineInDom) {
          setIsActionsTooltipVisible(false);
          setisAddNodeTooltipOpen(false);
        }
      }}
    >
      {
        canEdit && isActionsTooltipVisible && !dragging ? (
          <Popover
            content={
              <NodeSuggestionsPanel
                id={id}
                type={type}
                isAddNodeTooltipOpen={isAddNodeTooltipOpen}
                onAfterAdd={(newNodeId) => {
                  setisAddNodeTooltipOpen(false);
                  //getNodes() inside openEditForm doesn't get new nodes instantly, timeout provides a 'tick' to populate the nodes correctly.
                  setTimeout(() => openEditForm(newNodeId));
                }}
              />
            }
            placement={popoverPlacement}
            trigger="click"
            overlayClassName="node-tooltip-popover"
            open={isAddNodeTooltipOpen}
            color="black"
          >
            <NodeActionsTooltip
              id={id}
              isVisible={isActionsTooltipVisible}
              isEditEnabled={isEditEnabled}
              isDeleteEnabled={isDeleteEnabled}
              isCloneEnabled={isCloneEnabled}
              isAddNodeTooltipOpen={isAddNodeTooltipOpen}
              setisAddNodeTooltipOpen={setisAddNodeTooltipOpen}
              selected={selected}
              type={type}
            />
          </Popover>
        ) : (
          <Fragment>
            {
              hasAppearances && (
                <NodeAppearances
                  appearances={nodeAppearances}
                />
              )
            }
          </Fragment>
        )
      }

      <span className="react-flow__node__content">
        {children}
      </span>
    </span>
  );
};

NodeActionsTooltip.propTypes = {
  id: PropTypes.string,
  selected: PropTypes.bool,
  isVisible: PropTypes.bool,
  isEditEnabled: PropTypes.bool,
  isDeleteEnabled: PropTypes.bool,
  isCloneEnabled: PropTypes.bool,
  isAddNodeTooltipOpen: PropTypes.bool,
  setisAddNodeTooltipOpen: PropTypes.func
};

NodeSuggestionsPanel.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  className: PropTypes.string,
  isAddNodeTooltipOpen: PropTypes.bool,
  isBeforeTabVisible: PropTypes.bool,
  sourceHandleToConnect: PropTypes.string,
  onSelect: PropTypes.func,
  onAfterAdd: PropTypes.func
};

NodeWrapper.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.node,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  isEditEnabled: PropTypes.bool,
  isDeleteEnabled: PropTypes.bool,
  isCloneEnabled: PropTypes.bool,
  data: PropTypes.object
};

export default memo(NodeWrapper);
